import type { ComponentPropsWithRef } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cx } from '../../../ui';

/**
 * THE BOARD'S COLUMN AND CARD, AS PARTS (BR-PARTS; DESIGN.md B1 "one part per visual thing").
 *
 * The 4 Oct root-cause analysis counted the board twice: the records board here and the
 * Onboarding page's step board (`app/fulfillment/onboarding/BoardView.tsx`) each drew their own
 * column box and card box. The two boards hold different things - a column here is one
 * option's own query with drag and drop, there it is the clients whose next step it is - so
 * what they share is the drawing, and the drawing lives here once: a column is a sunken,
 * rounded box with a hairline that turns accent while a card is over it, a card is a white
 * rounded box with a hairline that darkens under the pointer.
 *
 * WIDTHS STAY THE CALLER'S, because they are the two boards' own rules and not the frame's:
 * the records board is a 320 px column on the desktop and a screen-wide snap stop on the phone
 * (`--board-column-width`), the step board a 236 px column and a slim 112 px one when empty
 * (register L14, R-K1). So are the contents, the header and the card's padding.
 */
const COLUMN = 'flex shrink-0 flex-col rounded-surface border bg-surface-sunken';
const CARD =
  'rounded-control border border-line bg-surface text-left transition-colors hover:border-line-strong';

export type KanbanColumnFrameProps = ComponentPropsWithRef<'section'> & {
  /** The column a dragged card would land in: the single accent, used for exactly that. */
  readonly highlighted?: boolean | undefined;
};

export function KanbanColumnFrame({
  highlighted = false,
  className,
  ...section
}: KanbanColumnFrameProps) {
  return (
    <section
      {...section}
      className={cx(
        COLUMN,
        highlighted ? 'border-accent bg-accent-subtle' : 'border-line-subtle',
        className,
      )}
    />
  );
}

/** A card that is a plain box, or the board's draggable `role="button"` node. */
type CardAsBox = ComponentPropsWithRef<'div'> & { readonly as?: 'div' | undefined };
/** A card that opens a page. */
type CardAsLink = LinkProps & { readonly as: 'link' };

export type KanbanCardFrameProps = CardAsBox | CardAsLink;

export function KanbanCardFrame(props: KanbanCardFrameProps) {
  if (props.as === 'link') {
    const { as: _link, className, ...link } = props;
    return <Link {...link} className={cx(CARD, className)} />;
  }
  const { as: _box, className, ...box } = props;
  return <div {...box} className={cx(CARD, className)} />;
}
